import { FC, useMemo, useState } from 'react';

import _ from 'underscore';
import { FormattedMessage, useIntl } from 'react-intl';

import Breadcrumbs from 'components/breadcrumbs';
import FilterSidebar from 'components/filter-sidebar';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Pagination from 'components/pagination';
import Switch from 'components/switch';
import SelfServiceTabsHeader from '../../components/self-service-tabs-header';
import EditFeed from './components/edit-feed';

import { Feed } from 'views/self-service/features/feed-catalog/types/feed.type';

import CreateFeed from './components/create-feed';

import { Box, Palette, useTheme } from '@mui/material';
import Paper from 'components/paper';
import TextField from 'components/text-field';
import { usePagination } from 'hooks/use-pagination';
import { Ban, CheckCircle, Search } from 'lucide-react';
import { loadState, saveState } from 'utils/storage';
import { FeedCatalogProvider, useFeedCatalogState } from './context/feed-catalog-context';

type FeedCatalogProps = {};

const getIcon = (hidden: boolean, color: Palette) => {
  const Icon = hidden ? Ban : CheckCircle;
  return <Icon color={hidden ? color.darkBlue.o25 : color.bluViu.main} size={20} strokeWidth={3} />;
};

type Filters = {
  searchValue: string;
  showActiveFeeds: boolean;
  showInactiveFeeds: boolean;
};

const defaultValues: Filters = {
  searchValue: '',
  showActiveFeeds: true,
  showInactiveFeeds: false,
};

const PAGE_NAME_ID = 'selfService.feedCatalog';

const FeedCatalog: FC<FeedCatalogProps> = ({}) => {
  const intl = useIntl();
  const theme = useTheme();
  const { allFeeds, isLoading, feedCodes } = useFeedCatalogState();

  // #region PAGINATION
  const PAGINATION_LIMIT = 15;
  // #endregion PAGINATION

  // #region STATE
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);
  const [filters, setFilters] = useState<Filters>(loadState({ key: 'feedFilters' }) || defaultValues);
  // #endregion STATE

  // #region OPTIONS
  const feedCatalogListOptions: HeadCell[] = [
    { id: 'name', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.name` }) },
    { id: 'feedBrandId', align: 'right', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.feedBrandId` }) },
    { id: 'feedType', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.feedType` }) },
    { id: 'feedSubType', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.feedSubType` }) },
    { id: 'size', align: 'right', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.size` }) },
    { id: 'price', align: 'right', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.price` }) },
    { id: 'createdBy', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.createdBy` }) },
    { id: 'hidden', align: 'center', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.active` }) },
    {
      id: 'actions',
      label: '',
      autoWidth: true,
    },
  ];
  // #endregion OPTIONS

  // #region HANDLERS

  const resetStates = () => {
    updateFilters(defaultValues);
  };

  const updateFilters = (newFilters: Filters) => {
    setFilters(newFilters);
    saveState({ key: 'feedFilters', state: newFilters });
  };
  // #endregion HANDLRES

  // #region RENDERS
  const feedsList = useMemo(() => {
    let rows: Feed[] | undefined = allFeeds;
    rows = _.chain(allFeeds)
      .filter((feed) => (filters.showActiveFeeds ? true : feed.hidden))
      .filter((feed) => (filters.showInactiveFeeds ? true : !feed.hidden))
      .filter((feed) => feed.name.toLowerCase().includes(filters.searchValue.toLowerCase()))
      .sortBy((feed) => feed.name.toLowerCase())
      .value();

    return rows;
  }, [filters, allFeeds]);
  // #endregion RENDERS

  return (
    <>
      <SelfServiceTabsHeader utils={<Breadcrumbs />} />
      <Box display={'flex'} justifyContent={'flex-end'}>
        <CreateFeed feedCodes={feedCodes} />
      </Box>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Paper transparent>
          <FilterSidebar
            sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
            showClearFilter={!!filters.searchValue || !filters.showActiveFeeds || filters.showInactiveFeeds}
            clearFilters={() => {
              resetStates();
            }}>
            <Box display={'flex'} flexDirection={'row'} gap={4}>
              <Switch
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showActive` })}
                value={filters.showActiveFeeds}
                onChange={() => {
                  handlePagination(1);
                  let newFilters = { ...filters, showActiveFeeds: !filters.showActiveFeeds };
                  updateFilters(newFilters);
                }}
              />
              <Switch
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showInactive` })}
                value={filters.showInactiveFeeds}
                onChange={() => {
                  handlePagination(1);
                  let newFilters = { ...filters, showInactiveFeeds: !filters.showInactiveFeeds };
                  updateFilters(newFilters);
                }}
              />
            </Box>

            <TextField
              value={filters.searchValue}
              label={intl.formatMessage({ id: 'general.search' })}
              onChange={(event) => {
                handlePagination(1);
                let newFilters = { ...filters, searchValue: event.target.value as string };

                updateFilters(newFilters);
              }}
              endAdornment={<Search size={18} />}
            />
          </FilterSidebar>
          <List
            isLoading={isLoading}
            noResult={{
              show: !feedsList.length,
              text: intl.formatMessage({ id: 'general.noResults' }),
            }}
            options={feedCatalogListOptions}>
            {feedsList.slice(from, to).map((feed, idx) => (
              <ListRow key={feed.id}>
                <ListCell align="left">{feed.name}</ListCell>
                <ListCell align="right" capitalize>
                  {feed.feedBrandId}
                </ListCell>
                <ListCell align="left" capitalize>
                  <FormattedMessage id={`${PAGE_NAME_ID}.types.${feed.feedType}`} />
                </ListCell>
                <ListCell align="left" capitalize>
                  <FormattedMessage id={`${PAGE_NAME_ID}.types.${feed.feedSubType}`} />
                </ListCell>
                <ListCell align="right">{feed.size}</ListCell>
                <ListCell align="right">{feed.price}</ListCell>
                <ListCell align="left">{feed.createdBy}</ListCell>
                <ListCell align="center">
                  {getIcon(feed.hidden, theme.palette)}
                </ListCell>
                <ListCell>
                  <EditFeed idx={idx} feed={feed} feeds={allFeeds} />
                </ListCell>
              </ListRow>
            ))}
          </List>
          {feedsList.length > PAGINATION_LIMIT && (
            <Pagination currentPage={currentPage} pages={Math.ceil(feedsList.length / PAGINATION_LIMIT)} onClick={handlePagination} />
          )}
        </Paper>
      </Box>
    </>
  );
};

const FeedCatalogContainer: React.FC<{}> = ({}) => {
  return (
    <FeedCatalogProvider>
      <FeedCatalog />
    </FeedCatalogProvider>
  );
};

export default FeedCatalogContainer;
